// data/items/weapons.js
// Combat weapons - swords, dirks, greatswords, bows, staffs

// Sword tier definitions (Smithing) - baseline melee weapon
// Combat level requirements roughly match smithing crafting levels
// Speeds 15% slower than original - 1H trades DPS for shield survivability
const SWORD_TIERS = {
    1: { prefix: 'Pyrestone', color: '#cd7f32', attack: 8, speed: 3200, accuracy: 5, combatLevel: 5, value: 120 },
    2: { prefix: 'Pale Shard', color: '#c0c0c0', attack: 16, speed: 3000, accuracy: 10, combatLevel: 20, value: 280 },
    3: { prefix: 'Blackiron', color: '#2d2d2d', attack: 28, speed: 2750, accuracy: 15, combatLevel: 35, value: 500 },
    4: { prefix: 'Skyiron', color: '#4169e1', attack: 44, speed: 2550, accuracy: 20, combatLevel: 50, value: 800 },
    5: { prefix: 'Verdant Core', color: '#228b22', attack: 64, speed: 2300, accuracy: 25, combatLevel: 65, value: 1200 },
    6: { prefix: 'Voidsteel', color: '#00ced1', attack: 88, speed: 2100, accuracy: 30, combatLevel: 80, value: 1800 }
};

// Dirk tier definitions (Smithing) - fast, weak melee weapon
// 70% attack, 130% speed (faster) compared to swords
// Speeds 15% slower - 1H trades DPS for shield survivability
const DIRK_TIERS = {
    1: { prefix: 'Pyrestone', color: '#cd7f32', attack: 6, speed: 2450, accuracy: 4, combatLevel: 1, value: 60 },
    2: { prefix: 'Pale Shard', color: '#c0c0c0', attack: 11, speed: 2300, accuracy: 7, combatLevel: 15, value: 140 },
    3: { prefix: 'Blackiron', color: '#2d2d2d', attack: 20, speed: 2150, accuracy: 11, combatLevel: 30, value: 250 },
    4: { prefix: 'Skyiron', color: '#4169e1', attack: 31, speed: 1950, accuracy: 14, combatLevel: 45, value: 400 },
    5: { prefix: 'Verdant Core', color: '#228b22', attack: 45, speed: 1800, accuracy: 18, combatLevel: 60, value: 600 },
    6: { prefix: 'Voidsteel', color: '#00ced1', attack: 62, speed: 1600, accuracy: 21, combatLevel: 75, value: 900 }
};

// Greatsword tier definitions (Smithing) - slow, powerful 2H melee weapon
// 165% attack, 70% speed (slower) compared to swords, blocks offhand
// Higher attack compensates for losing offhand slot and fewer procs/sec
// Speeds 20% FASTER than original to compensate for no multi-hit procs and losing offhand
const GREATSWORD_TIERS = {
    1: { prefix: 'Pyrestone', color: '#cd7f32', attack: 13, speed: 3200, accuracy: 7, combatLevel: 10, value: 180 },
    2: { prefix: 'Pale Shard', color: '#c0c0c0', attack: 26, speed: 2950, accuracy: 14, combatLevel: 25, value: 420 },
    3: { prefix: 'Blackiron', color: '#2d2d2d', attack: 46, speed: 2700, accuracy: 21, combatLevel: 40, value: 750 },
    4: { prefix: 'Skyiron', color: '#4169e1', attack: 73, speed: 2500, accuracy: 28, combatLevel: 55, value: 1200 },
    5: { prefix: 'Verdant Core', color: '#228b22', attack: 106, speed: 2250, accuracy: 35, combatLevel: 70, value: 1800 },
    6: { prefix: 'Voidsteel', color: '#00ced1', attack: 145, speed: 2050, accuracy: 42, combatLevel: 85, value: 2700 }
};

// Melee weapon sprites - tier -> sprite data (HD individual files)
const SWORD_SPRITES = {
    1: { sheet: 'assets/icons/equipment/meleeWeapons/pyrestone_sword.png', x: 0, y: 0, width: 64, height: 64 },
    2: { sheet: 'assets/icons/equipment/meleeWeapons/paleshard_sword.png', x: 0, y: 0, width: 64, height: 64 },
    3: { sheet: 'assets/icons/equipment/meleeWeapons/blackiron_sword.png', x: 0, y: 0, width: 64, height: 64 },
    4: { sheet: 'assets/icons/equipment/meleeWeapons/skyiron_sword.png', x: 0, y: 0, width: 64, height: 64 },
    5: { sheet: 'assets/icons/equipment/meleeWeapons/verdantcore_sword.png', x: 0, y: 0, width: 64, height: 64 },
    6: { sheet: 'assets/icons/equipment/meleeWeapons/voidsteel_sword.png', x: 0, y: 0, width: 64, height: 64 }
};

const DIRK_SPRITES = {
    1: { sheet: 'assets/icons/equipment/meleeWeapons/pyrestone_dirk.png', x: 0, y: 0, width: 64, height: 64 },
    2: { sheet: 'assets/icons/equipment/meleeWeapons/paleshard_dirk.png', x: 0, y: 0, width: 64, height: 64 },
    3: { sheet: 'assets/icons/equipment/meleeWeapons/blackiron_dirk.png', x: 0, y: 0, width: 64, height: 64 },
    4: { sheet: 'assets/icons/equipment/meleeWeapons/skyiron_dirk.png', x: 0, y: 0, width: 64, height: 64 },
    5: { sheet: 'assets/icons/equipment/meleeWeapons/verdantcore_dirk.png', x: 0, y: 0, width: 64, height: 64 },
    6: { sheet: 'assets/icons/equipment/meleeWeapons/voidsteel_dirk.png', x: 0, y: 0, width: 64, height: 64 }
};

const GREATSWORD_SPRITES = {
    1: { sheet: 'assets/icons/equipment/meleeWeapons/pyrestone_greatsword.png', x: 0, y: 0, width: 64, height: 64 },
    2: { sheet: 'assets/icons/equipment/meleeWeapons/paleshard_greatsword.png', x: 0, y: 0, width: 64, height: 64 },
    3: { sheet: 'assets/icons/equipment/meleeWeapons/blackiron_greatsword.png', x: 0, y: 0, width: 64, height: 64 },
    4: { sheet: 'assets/icons/equipment/meleeWeapons/skyiron_greatsword.png', x: 0, y: 0, width: 64, height: 64 },
    5: { sheet: 'assets/icons/equipment/meleeWeapons/verdantcore_greatsword.png', x: 0, y: 0, width: 64, height: 64 },
    6: { sheet: 'assets/icons/equipment/meleeWeapons/voidsteel_greatsword.png', x: 0, y: 0, width: 64, height: 64 }
};

// Generate sword items
function generateSwords() {
    const swords = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = SWORD_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        const item = {
            id: `${prefixId}_sword`,
            name: `${tierData.prefix} Sword`,
            color: tierData.color,
            tier: tier,
            slot: 'weapon',
            attackStyle: 'melee',
            attackBonus: tierData.attack,
            attackSpeed: tierData.speed,
            meleeAccuracy: tierData.accuracy,
            combatLevel: tierData.combatLevel,
            value: tierData.value
        };

        if (SWORD_SPRITES[tier]) {
            item.sprite = { ...SWORD_SPRITES[tier] };
        }

        swords.push(item);
    }

    return swords;
}

// Generate dirk items - fast, weak daggers
function generateDirks() {
    const dirks = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = DIRK_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        const item = {
            id: `${prefixId}_dirk`,
            name: `${tierData.prefix} Dirk`,
            color: tierData.color,
            tier: tier,
            slot: 'weapon',
            attackStyle: 'melee',
            attackBonus: tierData.attack,
            attackSpeed: tierData.speed,
            meleeAccuracy: tierData.accuracy,
            combatLevel: tierData.combatLevel,
            value: tierData.value
        };

        if (DIRK_SPRITES[tier]) {
            item.sprite = { ...DIRK_SPRITES[tier] };
        }

        dirks.push(item);
    }

    return dirks;
}

// Generate greatsword items - slow, powerful 2H weapons
function generateGreatswords() {
    const greatswords = [];

    for (let tier = 1; tier <= 6; tier++) {
        const tierData = GREATSWORD_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        const item = {
            id: `${prefixId}_greatsword`,
            name: `${tierData.prefix} Greatsword`,
            color: tierData.color,
            tier: tier,
            slot: 'weapon',
            twoHanded: true,  // Blocks offhand slot when equipped
            attackStyle: 'melee',
            attackBonus: tierData.attack,
            attackSpeed: tierData.speed,
            meleeAccuracy: tierData.accuracy,
            combatLevel: tierData.combatLevel,
            value: tierData.value
        };

        if (GREATSWORD_SPRITES[tier]) {
            item.sprite = { ...GREATSWORD_SPRITES[tier] };
        }

        greatswords.push(item);
    }

    return greatswords;
}

// Staff tier definitions (Woodworking + Glyphbinding)
// Names match log tiers for crafting consistency
// magicDamage = spell damage multiplier (affects spell base damage)
const STAFF_TIERS = {
    1: { prefix: 'Common', color: '#DEB887', attack: 6, speed: 4200, accuracy: 8, combatLevel: 1, value: 80, magicDamage: 1.0 },
    2: { prefix: 'Ironwood', color: '#A0522D', attack: 12, speed: 3900, accuracy: 14, combatLevel: 15, value: 200, magicDamage: 1.1 },
    3: { prefix: 'Softbark', color: '#6B8E23', attack: 22, speed: 3600, accuracy: 20, combatLevel: 30, value: 380, magicDamage: 1.2 },
    4: { prefix: 'Goldleaf', color: '#D2691E', attack: 36, speed: 3400, accuracy: 28, combatLevel: 45, value: 640, magicDamage: 1.35 },
    5: { prefix: 'Redgrain', color: '#CD853F', attack: 46, speed: 3200, accuracy: 32, combatLevel: 55, value: 850, magicDamage: 1.5 },
    6: { prefix: 'Gravewood', color: '#228B22', attack: 58, speed: 3100, accuracy: 38, combatLevel: 70, value: 1150, magicDamage: 1.7 },
    7: { prefix: 'Arcanewood', color: '#9370DB', attack: 76, speed: 2900, accuracy: 45, combatLevel: 85, value: 1600, magicDamage: 2.0 }
};

// Staff tiers with HD art in assets/icons/equipment/magicWeapons/.
// All 7 tiers have art (Common → Arcanewood).
const STAFF_SPRITE_TIERS = new Set([1, 2, 3, 4, 5, 6, 7]);

// Generate staff items
function generateStaffs() {
    const staffs = [];

    for (let tier = 1; tier <= 7; tier++) {
        const tierData = STAFF_TIERS[tier];
        const prefixId = tierData.prefix.toLowerCase().replace(/ /g, '_');

        const staff = {
            id: `${prefixId}_staff`,
            name: `${tierData.prefix} Staff`,
            icon: '🪄',  // Emoji fallback for tiers without HD art yet
            color: tierData.color,
            tier: tier,
            slot: 'weapon',
            attackStyle: 'magic',
            attackBonus: tierData.attack,
            attackSpeed: tierData.speed,
            magicAccuracy: tierData.accuracy,
            magicDamage: tierData.magicDamage,  // Spell damage multiplier
            combatLevel: tierData.combatLevel,
            value: tierData.value
        };

        if (STAFF_SPRITE_TIERS.has(tier)) {
            staff.sprite = {
                sheet: `assets/icons/equipment/magicWeapons/${prefixId}_staff.png`,
                x: 0, y: 0, width: 64, height: 64
            };
        }

        staffs.push(staff);
    }

    return staffs;
}

// Export weapons
export const SWORDS = generateSwords();
export const DIRKS = generateDirks();
export const GREATSWORDS = generateGreatswords();
export const STAFFS = generateStaffs();

// Starter weapons - weaker versions available from shop
// These are intentionally weaker than crafted T1 weapons to incentivize crafting
export const STARTER_WEAPONS = [
    {
        id: 'training_sword',
        name: 'Training Sword',
        color: '#808080',
        tier: 0,  // Below tier 1
        slot: 'weapon',
        attackStyle: 'melee',
        attackBonus: 4,      // Weaker than Pyrestone (8)
        attackSpeed: 4000,   // Slower than Pyrestone (3600)
        meleeAccuracy: 2,    // Lower than Pyrestone (5)
        combatLevel: 1,
        shopItem: true,       // Marks as purchasable from shop
        value: 25
    },
    {
        id: 'practice_bow',
        name: 'Practice Bow',
        color: '#808080',
        tier: 0,
        slot: 'weapon',
        attackStyle: 'ranged',
        attackBonus: 3,      // Weaker than Shortbow (5)
        attackSpeed: 4000,   // Slower than Shortbow (3600)
        rangedAccuracy: 3,   // Lower than Shortbow (6)
        combatLevel: 1,
        shopItem: true,
        value: 20,
        // Built-in weak arrows - no ammo consumption required
        builtInAmmo: {
            name: 'Worn Arrows',
            attackBonus: 1,       // Weaker than Pyrestone Arrows (+2)
            rangedAccuracy: 1     // Weaker than Pyrestone Arrows (+3)
        }
    },
    {
        id: 'apprentice_staff',
        name: 'Apprentice Staff',
        color: '#808080',
        tier: 0,
        slot: 'weapon',
        attackStyle: 'magic',
        attackBonus: 3,      // Weaker than Common Staff (6)
        attackSpeed: 4600,   // Slower than Common Staff (4200)
        magicAccuracy: 4,    // Lower than Common Staff (8)
        magicDamage: 0.9,    // Lower than Common Staff (1.0)
        combatLevel: 1,
        shopItem: true,
        value: 15,
        // Built-in weak fire spell - no glyph consumption required
        builtInSpell: {
            name: 'Minor Flame',
            baseDamage: 4,        // Weaker than Gust Strike (6) or Ember Strike (12)
            element: 'fire',
            description: 'A weak flame conjured by the apprentice staff.'
        }
    }
];

// Combined weapons array for item registry
export const WEAPONS = [...SWORDS, ...DIRKS, ...GREATSWORDS, ...STAFFS, ...STARTER_WEAPONS];

// Helper to get weapons by style
export function getWeaponsByStyle(style) {
    return WEAPONS.filter(weapon => weapon.attackStyle === style);
}

// Helper to get weapon by tier
export function getWeaponByTier(tier, style = null) {
    if (style) {
        return WEAPONS.filter(w => w.tier === tier && w.attackStyle === style);
    }
    return WEAPONS.filter(w => w.tier === tier);
}

export default WEAPONS;
